import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  HostListener,
  OnInit,
  OnDestroy,
  TemplateRef,
  afterNextRender,
  computed,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import type { ClassValue } from 'clsx';
import { MOBILE_BREAKPOINT } from 'src/app/@core/constants/breakpoints';
import { mergeClasses } from 'src/app/@core/utils/merge-classes';
import { HlmDialogComponent } from '../../spartan/dialog/hlm-dialog.component';
import { HlmDialogContentComponent } from '../../spartan/dialog/hlm-dialog-content.component';
import { HlmDialogHeaderComponent } from '../../spartan/dialog/hlm-dialog-header.component';
import { HlmDialogFooterComponent } from '../../spartan/dialog/hlm-dialog-footer.component';
import { HlmDialogTitleDirective } from '../../spartan/dialog/hlm-dialog-title.directive';
import { HlmDialogDescriptionDirective } from '../../spartan/dialog/hlm-dialog-description.directive';
import { HlmDialogPortalDirective } from '../../spartan/dialog/hlm-dialog-portal.directive';

/**
 * Arena dialog wrapper (`app-dialog`).
 *
 * Wraps Spartan helm dialog with Arena-specific defaults and mobile responsiveness.
 * On mobile (< 1024px) with `fullscreenOnMobile` enabled (default), the dialog
 * renders fullscreen (100vw × 100dvh) instead of the centered modal.
 *
 * ### Content slots
 * - `#headerTemplate` — custom header (falls back to `title` + `description` inputs)
 * - `#bodyTemplate` — main content (required)
 * - `#footerTemplate` — action buttons (optional)
 *
 * ### Usage
 * ```html
 * <app-dialog [(visible)]="isOpen" title="Edit profile" description="Make changes...">
 *   <ng-template #bodyTemplate>
 *     <p>Body content here</p>
 *   </ng-template>
 *   <ng-template #footerTemplate>
 *     <app-button (clicked)="isOpen.set(false)">Save</app-button>
 *   </ng-template>
 * </app-dialog>
 * ```
 */
@Component({
  selector: 'app-dialog',
  standalone: true,
  host: {
    style: 'display: contents',
  },
  imports: [
    CommonModule,
    HlmDialogComponent,
    HlmDialogContentComponent,
    HlmDialogHeaderComponent,
    HlmDialogFooterComponent,
    HlmDialogTitleDirective,
    HlmDialogDescriptionDirective,
    HlmDialogPortalDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <hlm-dialog
      [state]="dialogState()"
      [closeOnOutsidePointerEvents]="closeOnOutsidePointerEvents()"
      [closeOnBackdropClick]="closable()"
      (stateChanged)="onStateChanged($event)"
    >
      <ng-content select="[dialogTrigger]" />

      <ng-template hlmDialogPortal>
        <hlm-dialog-content
          [showCloseButton]="showCloseButton()"
          [class]="contentClass()"
        >
          @if (headerTemplate) {
            <ng-container *ngTemplateOutlet="headerTemplate" />
          } @else if (title()) {
            <hlm-dialog-header>
              <h3 hlmDialogTitle>{{ title() }}</h3>
              @if (description()) {
                <p hlmDialogDescription>{{ description() }}</p>
              }
            </hlm-dialog-header>
          }

          <div [class]="bodyClass()">
            @if (bodyTemplate) {
              <ng-container *ngTemplateOutlet="bodyTemplate" />
            }
            <ng-content />
          </div>

          @if (footerTemplate) {
            <hlm-dialog-footer>
              <ng-container *ngTemplateOutlet="footerTemplate" />
            </hlm-dialog-footer>
          }
        </hlm-dialog-content>
      </ng-template>
    </hlm-dialog>
  `,
})
export class DialogComponent implements OnInit, OnDestroy {
  @ContentChild('headerTemplate') headerTemplate?: TemplateRef<any>;
  @ContentChild('bodyTemplate') bodyTemplate?: TemplateRef<any>;
  @ContentChild('footerTemplate') footerTemplate?: TemplateRef<any>;

  readonly visible = model<boolean>(false);

  /**
   * Guards against the BrnDialog timing issue: BrnDialog.open() bails out if
   * _contentTemplate (registered by *hlmDialogPortal) isn't set yet. That
   * registration happens after the constructor effect fires, so if visible=true
   * on first render the dialog never opens. Holding state at 'closed' until
   * afterNextRender ensures we always get a 'closed'→'open' transition AFTER
   * the portal template is fully registered.
   */
  private readonly _viewReady = signal(false);
  readonly dialogState = computed(() =>
    this._viewReady() && this.visible() ? 'open' : 'closed',
  );

  constructor() {
    afterNextRender(() => this._viewReady.set(true));
  }
  readonly title = input<string>('');
  readonly description = input<string>('');
  readonly showCloseButton = input<boolean>(true);
  readonly closable = input<boolean>(true);
  readonly closeOnOutsidePointerEvents = input<boolean>(false);
  /** @deprecated Pass a static Tailwind `max-w-*` class via `class` instead — dynamic class values are not scanned by Tailwind JIT. */
  readonly width = input<string>('32rem');
  readonly fullscreenOnMobile = input<boolean>(true);
  readonly userClass = input<ClassValue>('', { alias: 'class' });

  readonly closed = output<void>();

  private readonly _windowWidth = signal<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200,
  );

  readonly isMobile = computed(
    () => this._windowWidth() < MOBILE_BREAKPOINT,
  );

  readonly contentClass = computed(() => {
    if (this.fullscreenOnMobile() && this.isMobile()) {
      return mergeClasses(
        '!fixed !inset-0 !w-screen !h-dvh !max-w-none !max-h-none !rounded-none !translate-x-0 !translate-y-0 !top-0 !left-0',
        'flex flex-col',
        this.userClass(),
      );
    }
    // Dynamic Tailwind classes are not scanned by JIT. Width is applied via
    // the hlm-dialog-content base (max-w-lg for default) or via a static
    // max-w-* class passed through `class` input by the caller.
    return mergeClasses('flex flex-col max-h-[90vh]', this.userClass());
  });

  readonly bodyClass = computed(() => {
    const base = 'flex-1 min-h-0 overflow-y-auto';
    return base;
  });

  @HostListener('window:resize')
  onResize(): void {
    this._windowWidth.set(window.innerWidth);
  }

  ngOnInit(): void {
    this._windowWidth.set(
      typeof window !== 'undefined' ? window.innerWidth : 1200,
    );
  }

  ngOnDestroy(): void { }

  onStateChanged(state: 'open' | 'closed'): void {
    const isOpen = state === 'open';
    this.visible.set(isOpen);
    if (!isOpen) {
      this.closed.emit();
    }
  }
}
